import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useDebounceValue } from '../../../../../hooks/useDebounceValue';
import { useEventLogging } from '../../../../../models/Logging';
import { useTagsQuery } from '../../../../../queries/Tag/useTagsQuery';
import { Button } from '../../../../components-v2/Button';
import { Checkbox } from '../../../../components-v2/Checkbox';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '../../../../components-v2/Drawer/Drawer';
import { Input } from '../../../../components-v2/Input';
import { Pill } from '../../../../components-v2/Pill';
import { Skeleton } from '../../../../components-v2/Skeleton';
import { styleSheet } from './styles';

const PageSize = 25;
const FilterDebounceMs = 300;
const SkeletonRowCount = 8;
const TagSort: Api.ISortDescriptor = { sort: 'asc', sortBy: 'name' };

interface IPanelProps {
	excludedTagNames?: ReadonlySet<string>;
	impersonationContext?: Api.IImpersonationContext;
	initialSelectedTagNames?: string[];
	onApply(selectedTags: Api.IAccountTag[]): void;
	onRequestClose(): void;
}

interface IProps extends IPanelProps {
	isOpen: boolean;
}

const getApplyLabel = (count: number) => {
	if (count === 0) {
		return 'Apply';
	}
	return count === 1 ? 'Apply 1 tag' : `Apply ${count} tags`;
};

function BrowseAllTagsPanel({
	excludedTagNames = Api.DefaultBulkSendExcludedTags,
	impersonationContext,
	initialSelectedTagNames = [],
	onApply,
	onRequestClose,
}: IPanelProps) {
	const { logEvent } = useEventLogging('BrowseAllTags');
	const [filter, setFilter] = React.useState('');
	const debouncedFilter = useDebounceValue(filter.trim(), FilterDebounceMs);
	const [selectedTagsByName, setSelectedTagsByName] = React.useState(
		() => new Map<string, Api.IAccountTag>(initialSelectedTagNames.map(name => [name, { name }]))
	);
	const tagsQuery = useTagsQuery({
		impersonationContext,
		pageSize: PageSize,
		query: debouncedFilter || undefined,
		sortDescriptor: TagSort,
	});
	const tags = (tagsQuery.data?.pages.flatMap(page => page.values) ?? []).filter(
		tag => !excludedTagNames.has(tag.name)
	);

	const toggleTag = (tag: Api.IAccountTag) => {
		setSelectedTagsByName(previous => {
			const next = new Map(previous);
			if (next.has(tag.name)) {
				next.delete(tag.name);
			} else {
				next.set(tag.name, tag);
			}
			return next;
		});
	};

	const handleApply = () => {
		const selectedTags = Array.from(selectedTagsByName.values());
		logEvent('Apply', { count: selectedTags.length });
		onApply(selectedTags);
	};

	const renderList = () => {
		if (tagsQuery.status === 'pending') {
			return Array.from({ length: SkeletonRowCount }, (_, index) => (
				<Skeleton key={`browse-tags-skeleton-${index}`} styles={[styleSheet.skeletonRow]} />
			));
		}
		if (tagsQuery.status === 'error') {
			return (
				<div className={css(styleSheet.message)}>
					<div>Couldn&apos;t load tags.</div>
					<Button variant='link' size='sm' onClick={() => tagsQuery.refetch()}>
						Try again
					</Button>
				</div>
			);
		}
		if (tags.length === 0) {
			return (
				<div className={css(styleSheet.message)}>
					{debouncedFilter ? `No tags match "${debouncedFilter}".` : 'This account has no tags yet.'}
				</div>
			);
		}
		return (
			<>
				{tags.map(tag => {
					const isSelected = selectedTagsByName.has(tag.name);
					return (
						<label className={css(styleSheet.row)} key={tag.id ?? tag.name}>
							<Checkbox checked={isSelected} onCheckedChange={() => toggleTag(tag)} />
							<div className={css(styleSheet.rowPill)}>
								<Pill variant='tag' color={tag.color} label={tag.name} />
							</div>
							{tag.stats?.contacts != null ? (
								<span className={css(styleSheet.contactCount)}>{tag.stats.contacts.toLocaleString()}</span>
							) : null}
						</label>
					);
				})}
				{tagsQuery.isFetchingNextPage ? <div className={css(styleSheet.loadingMore)}>Loading more tags…</div> : null}
				{tagsQuery.hasNextPage && !tagsQuery.isFetchingNextPage ? (
					<Waypoint bottomOffset='-200px' onEnter={() => tagsQuery.fetchNextPage()} />
				) : null}
			</>
		);
	};

	return (
		<>
			<DrawerHeader>
				<DrawerTitle>All Tags</DrawerTitle>
				<DrawerDescription>Select one or more tags to send to.</DrawerDescription>
			</DrawerHeader>
			<div className={css(styleSheet.body)}>
				<Input
					aria-label='Filter tags'
					autoFocus
					onChange={event => setFilter(event.target.value)}
					placeholder='Filter tags'
					styles={[styleSheet.filterInput]}
					value={filter}
				/>
				<div className={css(styleSheet.list)}>{renderList()}</div>
			</div>
			<DrawerFooter styles={[styleSheet.footer]}>
				<Button variant='secondary' onClick={onRequestClose}>
					Cancel
				</Button>
				<Button onClick={handleApply}>{getApplyLabel(selectedTagsByName.size)}</Button>
			</DrawerFooter>
		</>
	);
}

export function BrowseAllTagsFlyout({ isOpen, onRequestClose, ...panelProps }: IProps) {
	return (
		<Drawer open={isOpen} onOpenChange={open => !open && onRequestClose()} swipeDirection='right'>
			<DrawerContent>
				<BrowseAllTagsPanel onRequestClose={onRequestClose} {...panelProps} />
			</DrawerContent>
		</Drawer>
	);
}
